import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import { CopyableId } from '../../components/ui/CopyableId'
import { StatusChip } from '../../components/ui/StatusChip'
import { requestMoney } from './money'
import { UNSET, formatDateTime, formatDuration, formatIrr } from '../../format'
import { outcomeLabel } from '../../vocabulary'
import type { ApiRequestRow } from '../../api/portal'
import { Ltr } from '../../components/ui/Ltr'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  )
}

/**
 * One request, in full.
 *
 * Everything here comes from the row the list already holds — there is no
 * detail endpoint, because the customer-facing serialiser has no field the
 * list omits. That is deliberate on the server's side and worth restating
 * here: the fields that do not exist in this response are the provider, its
 * cost, the margin, and the request and response bodies.
 *
 * The bodies are not withheld, they are not stored. A customer asking us what
 * they sent is a question we cannot answer, which is the correct answer for a
 * platform that carries national identifiers and card numbers through itself.
 *
 * What is here is what a customer needs to reconcile: the identifier to quote,
 * what it cost, what came back, and how long it took.
 */
export function RequestDetail({
  request,
  onClose,
}: {
  request: ApiRequestRow | null
  onClose: () => void
}) {
  const outcome = request ? outcomeLabel(request.error_code) : null
  const money = request ? requestMoney(request) : null

  return (
    // MUI mirrors left/right anchors under an RTL theme, so "right" here is
    // the physical left of the screen — the side the navigation is not on.
    <Drawer anchor="right" open={request !== null} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 } }} role="region" aria-label="جزئیات درخواست">
        {/* The app bar is fixed and sits above every drawer, so a panel that
            starts at the top of the viewport starts underneath it. The
            navigation drawer solves this the same way. */}
        <Toolbar />
        <Box sx={{ p: 3, pt: 0 }}>
          {request ? (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h2" gutterBottom>
                  جزئیات درخواست
                </Typography>
                <StatusChip status={request.status} />
              </Box>

              {outcome ? (
                <Alert severity={request.status === 'succeeded' ? 'info' : 'warning'}>
                  {outcome}
                </Alert>
              ) : null}

              <Divider />

              <Field label="شناسهٔ پیگیری">
                <CopyableId value={request.request_id} visible={16} label="شناسهٔ پیگیری" />
              </Field>
              <Field label="زمان ثبت">{formatDateTime(request.created_at)}</Field>
              <Field label="زمان پایان">
                {request.completed_at ? formatDateTime(request.completed_at) : 'هنوز پایان نیافته'}
              </Field>
              <Field label="مدت">{formatDuration(request.duration_ms)}</Field>

              <Divider />

              <Field label="اپلیکیشن">{request.application_name}</Field>
              {/* The name a customer knows, with the identifier beneath it —
                  the same order as the table this drawer opens from. */}
              <Field label="سرویس">
                <Typography variant="body2">
                  {request.operation_display_name_fa || request.operation_code}
                </Typography>
                {request.operation_display_name_fa ? (
                  <Ltr
                    component="code"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {request.operation_code}
                  </Ltr>
                ) : null}
              </Field>

              <Divider />

              {money ? (
                <>
                  <Field label="بهای اعلام‌شده">{formatIrr(money.quotedIrr)}</Field>
                  <Field label="کسرشده">{formatIrr(money.chargedIrr)}</Field>
                  <Field label="بازگشت‌داده‌شده">
                    {money.refundedIrr > 0 ? formatIrr(money.refundedIrr) : UNSET.amount}
                  </Field>
                  {/* The one line that answers «این فراخوانی با موجودی من چه
                      کرد» without the reader doing the subtraction. */}
                  <Field label="اثر نهایی بر موجودی">
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        color: money.netIrr > 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {money.netIrr > 0 ? '+\u200e' : ''}
                      {formatIrr(money.netIrr)}
                    </Typography>
                  </Field>
                </>
              ) : null}

              {/* The stored code, kept because it is what a support
                  conversation quotes — under the sentence, not instead of it. */}
              {request.error_code ? (
                <Field label="کد فنی نتیجه">
                  <Ltr component="code" >
                    {request.error_code}
                  </Ltr>
                </Field>
              ) : null}

              <Typography variant="caption" color="text.secondary">
                محتوای درخواست و پاسخ نگهداری نمی‌شود، بنابراین در اینجا قابل نمایش نیست.
              </Typography>

              <Button onClick={onClose} variant="outlined">
                بستن
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  )
}
